import React, {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
} from "react";
import { BotStatus, DisplayResultMessage, OpenMessage } from "@shared/types";
import useBotsBulkActions from "@shared/features/bots/useBotsBulkActions";
import {
  BulkDeleteBotsModalProps,
  BulkUpdateBotsModalProps,
} from "@shared/features/bots/types";
import { UseQuotaModal } from "@shared/features/settings/types";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";

interface MyBotsPageProviderProps {
  openMessage: OpenMessage;
  openActionMessage?: OpenMessage;
  dropdownAlertComponent?: ReactNode;
  navigateToBulkEdit?: (botsIds: number[]) => void;
  BulkDeleteBotsModal?: FC<BulkDeleteBotsModalProps>;
  BulkUpdateBotsModal?: FC<BulkUpdateBotsModalProps>;
  displayResultMessage?: DisplayResultMessage;
  useQuotaModal?: UseQuotaModal;
}

const BotsBulkActionsProvider = ({
  openMessage,
  openActionMessage,
  dropdownAlertComponent,
  navigateToBulkEdit,
  BulkDeleteBotsModal,
  BulkUpdateBotsModal,
  displayResultMessage,
  useQuotaModal,
  children,
}: PropsWithChildren<MyBotsPageProviderProps>) => {
  const { botType } = useContext(MyBotsPageContext)!;
  const { deleteBotsModalData, quotaModal, ...bulkActionsData } =
    useBotsBulkActions({
      openMessage,
      openActionMessage,
      navigateToBulkEdit,
      displayResultMessage,
      useQuotaModal,
    });
  const {
    setBulkUpdateBotsModalOpen,
    selectedBotsIDs,
    bulkUpdateBotsModalOpen,
    selectedBotStatus,
    exitBulkUpdate,
    botIDToBotData,
  } = bulkActionsData;

  return (
    <BotsBulkActionsContext.Provider
      value={{
        ...bulkActionsData,
        dropdownAlertComponent,
        openActionMessage,
      }}
    >
      {children}
      {BulkDeleteBotsModal && (
        <BulkDeleteBotsModal
          isOpen={deleteBotsModalData.isOpen}
          closeModal={deleteBotsModalData.closeModal}
          isLoading={deleteBotsModalData.isLoading}
          content={deleteBotsModalData.content}
          onOk={deleteBotsModalData.onOk}
        />
      )}
      {BulkUpdateBotsModal && (
        <BulkUpdateBotsModal
          closeModal={() => setBulkUpdateBotsModalOpen(false)}
          botsIDs={selectedBotsIDs}
          open={bulkUpdateBotsModalOpen}
          isDeactivate={selectedBotStatus === BotStatus.ACTIVE}
          onFinish={exitBulkUpdate}
          botType={botType}
          botIDToBotData={botIDToBotData}
        />
      )}
      {quotaModal}
    </BotsBulkActionsContext.Provider>
  );
};

export default BotsBulkActionsProvider;

type BulkActionsData = Omit<
  ReturnType<typeof useBotsBulkActions>,
  "deleteBotsModalData" | "quotaModal"
>;
export const BotsBulkActionsContext = createContext<
  | (BulkActionsData & {
      dropdownAlertComponent?: ReactNode;
      openActionMessage?: OpenMessage;
    })
  | undefined
>(undefined);
